import { useMemo } from "react";
import { isValid, parseISO } from "date-fns";
import { LuArrowLeft, LuFilter, LuSearch } from "react-icons/lu";
import {
  DataTable,
  DataTableContent,
  DataTableFooter,
  DataTablePagination,
  DataTableRangeInfo,
  DataTableSkeleton,
  type ColumnDef,
} from "@/components/data-table";
import { Heading, Muted } from "@/components/typography";
import { AsyncBoundary } from "@/components/ui/async-boundary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Empty, EmptyDescription, EmptyIcon, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTab } from "@/components/ui/tabs";
import useLocalStorageState from "@/hooks/state/useLocalStorageState";
import { useDevices, useReviewScripts } from "../hooks/use-review";
import { troubledDeviceCount } from "../lib/device-status";
import type { ReviewScriptListItem, ReviewStatus } from "../lib/review-client";

const DEFAULT_SORTING = [{ id: "createdAt", desc: true }];
const STORAGE_SEARCH = "review-script-list-search";
const STORAGE_TAB = "review-script-list-tab";
const STORAGE_SCANNERS = "review-script-list-scanners";
const STORAGE_SUBJECTS = "review-script-list-subjects";

type ReviewTab = "work" | "flagged" | "archive";

function isReviewTab(v: unknown): v is ReviewTab {
  return v === "work" || v === "flagged" || v === "archive";
}

const DHAKA_TIME_ZONE = "Asia/Dhaka";

const createdAtFormatter = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: DHAKA_TIME_ZONE,
});

const STATUS_BADGE_VARIANT: Record<ReviewStatus, "secondary" | "yellow" | "green"> = {
  pending: "secondary",
  flagged: "yellow",
  approved: "green",
};

const STATUS_ROW_ACCENT: Record<ReviewStatus, string | undefined> = {
  pending: undefined,
  flagged: "border-l-2 border-l-yellow-500",
  approved: "border-l-2 border-l-green-500",
};

/** Filter options come from the scripts on screen, not from the suggestion
 * endpoint: a value nobody has used yet would only ever filter to nothing. */
function distinctValues(
  scripts: ReviewScriptListItem[] | undefined,
  pick: (s: ReviewScriptListItem) => string | null,
): string[] {
  if (!scripts) return [];
  const values = new Set<string>();
  for (const s of scripts) {
    const value = pick(s)?.trim();
    if (value) values.add(value);
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}

function CheckboxFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (update: (prev: string[]) => string[]) => void;
}) {
  if (options.length === 0) return null;
  function toggle(value: string, checked: boolean) {
    onChange(prev => {
      const list = Array.isArray(prev) ? prev : [];
      if (checked) return list.includes(value) ? list : [...list, value];
      return list.filter(v => v !== value);
    });
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button type="button" variant="outline" className="shrink-0" />}>
        <LuFilter />
        {label}
        {selected.length > 0 ? <Badge variant="secondary">{selected.length}</Badge> : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
        {options.map(value => (
          <DropdownMenuCheckboxItem
            key={value}
            checked={selected.includes(value)}
            onCheckedChange={checked => toggle(value, checked === true)}
          >
            {value}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function makeColumns(onOpenScript: (id: string) => void): ColumnDef<ReviewScriptListItem>[] {
  return [
    {
      accessorFn: row => row.label || row.id,
      header: "Script",
      id: "label",
      cell: ({ row }) => (
        <button
          className="block w-full text-left font-medium hover:underline"
          onClick={() => onOpenScript(row.original.id)}
          type="button"
        >
          {row.original.label || row.original.id.slice(0, 8)}
        </button>
      ),
    },
    {
      accessorKey: "studentName",
      header: "Student",
      cell: ({ getValue }) => (getValue() as string | null) ?? "—",
    },
    {
      accessorKey: "subject",
      header: "Subject",
      cell: ({ getValue }) => (getValue() as string | null) ?? "—",
    },
    { accessorKey: "scannerName", header: "Scanner" },
    { accessorKey: "pageCount", header: "Pages" },
    {
      accessorKey: "reviewStatus",
      header: "Status",
      cell: ({ row, getValue }) => {
        const status = getValue() as ReviewStatus;
        const resolved = status === "flagged" && row.original.flagAckedAt;
        const unseen = status === "flagged" && !row.original.flagAckedAt;
        return (
          <div className="flex items-center gap-1.5">
            <Badge variant={STATUS_BADGE_VARIANT[status]}>{status}</Badge>
            {unseen && <span className="text-muted-foreground text-xs">not seen</span>}
            {resolved && <span className="text-muted-foreground text-xs">Resolved by scanner</span>}
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      cell: ({ getValue }) => {
        const date = parseISO(String(getValue()));
        return isValid(date) ? createdAtFormatter.format(date) : "—";
      },
      header: "Created",
    },
  ];
}

export function ReviewScriptList({
  onOpenScript,
  onBack,
  onOpenLeaderboard,
  onOpenDevices,
}: {
  onOpenScript: (scriptId: string) => void;
  onBack: () => void;
  onOpenLeaderboard: () => void;
  onOpenDevices: () => void;
}) {
  const scriptsQuery = useReviewScripts();
  // A phone stuck holding a hundred pages shows up as nothing here — the
  // scripts simply never arrive. The count on the Devices link is what
  // turns that absence into something the desk can see.
  const devicesQuery = useDevices();
  const troubled = troubledDeviceCount(devicesQuery.data, Date.now());
  const [search, setSearch] = useLocalStorageState(STORAGE_SEARCH, "");
  const [tab, setTab] = useLocalStorageState<ReviewTab>(STORAGE_TAB, "work");
  const [selectedScanners, setSelectedScanners] = useLocalStorageState<string[]>(STORAGE_SCANNERS, []);
  const [selectedSubjects, setSelectedSubjects] = useLocalStorageState<string[]>(STORAGE_SUBJECTS, []);
  const columns = useMemo(() => makeColumns(onOpenScript), [onOpenScript]);

  const safeTab = isReviewTab(tab) ? tab : "work";
  const safeScanners = Array.isArray(selectedScanners) ? selectedScanners : [];
  const safeSubjects = Array.isArray(selectedSubjects) ? selectedSubjects : [];
  const safeSearch = typeof search === "string" ? search : "";

  const scannerOptions = useMemo(
    () => distinctValues(scriptsQuery.data, s => s.scannerName),
    [scriptsQuery.data],
  );
  const subjectOptions = useMemo(
    () => distinctValues(scriptsQuery.data, s => s.subject),
    [scriptsQuery.data],
  );

  const filtered = useMemo(() => {
    const scripts = scriptsQuery.data;
    if (!scripts) return [];
    const q = safeSearch.trim().toLowerCase();
    const scannerSet = safeScanners.length > 0 ? new Set(safeScanners) : null;
    const subjectSet = safeSubjects.length > 0 ? new Set(safeSubjects) : null;
    return scripts.filter(s => {
      const matchesTab =
        safeTab === "work"
          ? s.reviewStatus === "pending"
          : safeTab === "flagged"
            ? s.reviewStatus === "flagged"
            : s.reviewStatus === "approved";
      const matchesSearch =
        !q ||
        (s.label ?? "").toLowerCase().includes(q) ||
        (s.studentName ?? "").toLowerCase().includes(q) ||
        s.tags.some(t => t.toLowerCase().includes(q));
      const matchesScanner = !scannerSet || scannerSet.has(s.scannerName);
      const matchesSubject = !subjectSet || subjectSet.has((s.subject ?? "").trim());
      return matchesTab && matchesSearch && matchesScanner && matchesSubject;
    });
  }, [scriptsQuery.data, safeSearch, safeTab, safeScanners, safeSubjects]);

  return (
    <div className="p-6 pb-20">
      <div className="mb-4 flex items-center gap-2">
        <button type="button" onClick={onBack} aria-label="Back" className="text-muted-foreground">
          <LuArrowLeft className="size-5" />
        </button>
        <div>
          <Heading as="h1" variant="h4">
            Review
          </Heading>
          <Muted>{`${filtered.length} scripts`}</Muted>
        </div>
        <button
          type="button"
          onClick={onOpenDevices}
          className="ml-auto flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
        >
          Devices
          {troubled > 0 ? <Badge variant="destructive">{troubled}</Badge> : null}
        </button>
        <button type="button" onClick={onOpenLeaderboard} className="text-sm text-primary underline-offset-4 hover:underline">
          Leaderboard
        </button>
      </div>

      <Tabs value={safeTab} onValueChange={v => setTab(v as ReviewTab)} className="mb-4">
        <TabsList>
          <TabsTab value="work">Work</TabsTab>
          <TabsTab value="flagged">Flagged</TabsTab>
          <TabsTab value="archive">Archive</TabsTab>
        </TabsList>
      </Tabs>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <LuSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            value={safeSearch}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search label, student, or tag"
          />
        </div>
        <CheckboxFilter
          label="Subject"
          options={subjectOptions}
          selected={safeSubjects}
          onChange={setSelectedSubjects}
        />
        <CheckboxFilter
          label="Scanner"
          options={scannerOptions}
          selected={safeScanners}
          onChange={setSelectedScanners}
        />
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        defaultSorting={DEFAULT_SORTING}
        enableRowSelection={false}
        pageIndexStorageKey="review-script-list-page-index"
        sortingStorageKey="review-script-list-sorting"
        tableOptions={{ getRowId: row => row.id }}
      >
        <AsyncBoundary query={scriptsQuery}>
          <AsyncBoundary.Pending>
            <DataTableSkeleton rows={5} />
          </AsyncBoundary.Pending>
          <AsyncBoundary.Error>
            {(error: unknown) => (
              <DataTableContent
                emptyState={
                  <Empty>
                    <EmptyTitle>Couldn't load scripts</EmptyTitle>
                    <EmptyDescription>{String(error)}</EmptyDescription>
                  </Empty>
                }
              />
            )}
          </AsyncBoundary.Error>
          <AsyncBoundary.Success>
            <DataTableContent<ReviewScriptListItem>
              rowClassName={row => STATUS_ROW_ACCENT[row.original.reviewStatus]}
              emptyState={
                <Empty>
                  <EmptyIcon>
                    <LuSearch />
                  </EmptyIcon>
                  <EmptyTitle>
                    {safeTab === "work" && "No scripts to review"}
                    {safeTab === "flagged" && "Nothing flagged"}
                    {safeTab === "archive" && "Nothing archived yet"}
                  </EmptyTitle>
                  <EmptyDescription>
                    {safeTab === "work" && "Flagged and approved scripts move to their own tabs."}
                    {safeTab === "flagged" && "Flag a script during review to see it here."}
                    {safeTab === "archive" && "Approve a script to see it here."}
                  </EmptyDescription>
                </Empty>
              }
            />
          </AsyncBoundary.Success>
        </AsyncBoundary>
        <DataTableFooter>
          <DataTableRangeInfo suffix="scripts" />
          <DataTablePagination />
        </DataTableFooter>
      </DataTable>
    </div>
  );
}
